import React, { memo } from "react";
import PropTypes from "prop-types";
import "./AddressEditor.css";

const BUSY_STATUSES = ["saving", "validating"];

function AddressEditorView({
    loading,
    originalAddress,
    editedAddress,
    status,
    validationError,
    isDeleted,
    onEditAddress,
    onSave,
    onBack,
}) {
    const isBusy = BUSY_STATUSES.includes(status);

    // ── Error / Deleted View ─────
    if (status === "error" || (isDeleted && validationError)) {
        return (
            <div className="address-editor">
                <div className="ae-error-container">
                    <div className="ae-error-box">
                        <div className="ae-error-message">
                            {validationError || "An error occurred during validation."}
                        </div>
                        <button className="ae-retry-button" onClick={onBack}>
                            {isDeleted ? "Back to Dashboard" : "Scan Again"}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ── Main View ──
    return (
        <div className="address-editor">
            <h2 className="ae-title">Confirm / Edit Address</h2>

            {loading && (
                <div className="ae-loading">Waiting for address from Google Maps...</div>
            )}

            {!loading && (
                <>
                    <label className="ae-label">Address detected from Google:</label>
                    <div className="ae-original">{originalAddress}</div>

                    <label className="ae-label">Edit address:</label>
                    <textarea
                        className="ae-textarea"
                        value={editedAddress}
                        onChange={(e) => onEditAddress(e.target.value)}
                        rows={4}
                        aria-label="Edit address"
                        disabled={isBusy}
                    />

                    <div className="ae-actions">
                        <button
                            className="ae-button"
                            onClick={onSave}
                            disabled={isBusy || status === "saved"}
                        >
                            {status === "saving" ? "Saving..." :
                                status === "validating" ? "Validating Address..." :
                                    "Save & Confirm Address"}
                        </button>

                        <button
                            className="ae-button ae-button--secondary"
                            onClick={onBack}
                            disabled={isBusy}
                        >
                            Back to Dashboard
                        </button>
                    </div>

                    {status === "saved" && (
                        <div className="ae-status ae-status-success">
                            Address Verified! Moving...
                        </div>
                    )}

                    {status === "validating" && (
                        <div className="ae-status ae-status-validating">
                            Verifying address with USPS...
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

AddressEditorView.propTypes = {
    loading: PropTypes.bool.isRequired,
    originalAddress: PropTypes.string.isRequired,
    editedAddress: PropTypes.string.isRequired,
    status: PropTypes.oneOf(["saving", "validating", "saved", "error", null]),
    validationError: PropTypes.string,
    isDeleted: PropTypes.bool.isRequired,
    onEditAddress: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    onBack: PropTypes.func.isRequired,
};

AddressEditorView.defaultProps = {
    status: null,
    validationError: null,
};

export default memo(AddressEditorView);
